import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Alert } from 'react-bootstrap';
import NotesHeader from './NotesHeader';
import NewNote from './NewNote';
import NoteItem from './NoteItem';
import useAlertFading from '../../hooks/useAlertFading';
import { useNotes } from '../../hooks/useNotes';
import { convert } from 'html-to-text';
import { formatUnixTimestampToHumanReadableFormat } from '../../../utils/DateUtils';

const NotesContainer = ({
    surveyYear,
    companyId,
    userId,
    participantName,
    onError
}) => {
    const [newNoteText, setNewNoteText] = useState('');
    const [creatingNoteOperation, setCreatingNoteOperation] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const [displaySuccess, setDisplaySuccess] = useState(false);
    const [fadingAlerts, setFadingAlerts] = useState({
        displayError: false,
        displaySuccess: false
    });
    const [editMode, setEditMode] = useState([]);
    const [deleteMode, setDeleteMode] = useState([]);
    const [noteLoading, setNoteLoading] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [notesYear, setNotesYear] = useState(surveyYear);

    const {
        notes,
        loadingNotes,
        fetchQuestionnaireNotes,
        createQuestionnaireNote,
        updateQuestionnaireNote,
        deleteQuestionnaireNote,
        handleChangeNote
    } = useNotes(notesYear, companyId, userId);

    useAlertFading(displayError, setDisplayError, 'displayError', setFadingAlerts);
    useAlertFading(displaySuccess, setDisplaySuccess, 'displaySuccess', setFadingAlerts);

    useEffect(() => {
        const fetchNotes = async () => {
            await fetchQuestionnaireNotes();
        };
        if (userId) {
            fetchNotes();
        }
    }, [userId, notesYear, fetchQuestionnaireNotes]);

    useEffect(() => {
        const initilizedBoolArr = (notes) => {
            const boolArr = [];
            for (let i = 0; i < notes?.length; i++) {
                boolArr.push(false);
            }
            return boolArr;
        };

        const boolArr = initilizedBoolArr(notes);
        setEditMode(boolArr);
        setDeleteMode(boolArr);
        setNoteLoading(boolArr);
    }, [notes]);

    useEffect(() => {
        if (userId && newNoteText !== undefined && newNoteText.trim() !== '') {
            localStorage.setItem(`draft_note_${companyId}_${userId}`, newNoteText);
        }
    }, [newNoteText, userId, companyId]);

    useEffect(() => {
        if (userId && companyId) {
            const savedNote = localStorage.getItem(`draft_note_${companyId}_${userId}`);
            if (savedNote) {
                setNewNoteText(savedNote);
            }
        }
    }, [userId, companyId]);

    const handleCreateNote = async () => {
        setDisplayError(false);
        setCreatingNoteOperation(true);
        const result = await createQuestionnaireNote(newNoteText);
        if (result.success) {
            setSuccessMessage("Note created successfully");
            setDisplaySuccess(true);
            if (userId && companyId) {
                localStorage.removeItem(`draft_note_${companyId}_${userId}`);
            }
        } else {
            setError(result.error);
            setDisplayError(true);
            onError(result.error);
        }
        setNewNoteText('');
        setCreatingNoteOperation(false);
    };

    const handleUpdateNote = async (uniqueId, noteText) => {
        const result = await updateQuestionnaireNote(uniqueId, noteText);
        if (result.success) {
            setSuccessMessage("Note updated successfully");
            setDisplaySuccess(true);
        } else {
            setError(result.error);
            setDisplayError(true);
            onError(result.error);
        }
    };

    const handleDeleteNote = async (uniqueId) => {
        const result = await deleteQuestionnaireNote(uniqueId);
        if (result.success) {
            setSuccessMessage("Note deleted successfully");
            setDisplaySuccess(true);
        } else {
            setError(result.error);
            setDisplayError(true);
            onError(result.error);
        }
    };

    const handleExportNotes = async () => {
        const formatNoteOutput = () => {
            const notesCopy = [...notes];
            for (const note of notesCopy) {
                const richText = note.noteText;
                const createdOn = note.createdOn;
                const modifiedOn = note.modifiedOn;
                note.noteText = convert(richText);
                note.createdOn = formatUnixTimestampToHumanReadableFormat(createdOn);
                note.modifiedOn = formatUnixTimestampToHumanReadableFormat(modifiedOn);
            }
            return notesCopy;
        };

        const plainTextNotes = formatNoteOutput();
        const nameNoSpaces = participantName.replaceAll(" ", "").trim();
        const fileName = `${nameNoSpaces}_Questionnaire_Notes`;
        const XLSX = await import('xlsx');
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(plainTextNotes);
        XLSX.utils.book_append_sheet(wb, ws, 'Questionnaire Notes');
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };

    const handleEnableEditMode = (index) => {
        const newEditArr = [...editMode];
        newEditArr[index] = true;
        setEditMode(newEditArr);
    };

    const handleExitEditMode = (index) => {
        const newEditArr = [...editMode];
        newEditArr[index] = false;
        setEditMode(newEditArr);
    };

    const handleEnableDeleteMode = (index) => {
        const newDeleteArr = [...deleteMode];
        newDeleteArr[index] = true;
        setDeleteMode(newDeleteArr);
    };

    const handleExitDeleteMode = (index) => {
        const newDeleteArr = [...deleteMode];
        newDeleteArr[index] = false;
        setDeleteMode(newDeleteArr);
    };

    return (
        <div className="notes-container">
            <NotesHeader notes={notes} onExportNotes={handleExportNotes} notesYear={notesYear} handleSetNotesYear={setNotesYear} />
            {displayError && (
                <Alert
                    variant="danger"
                    dismissible
                    className={`status-alert ${fadingAlerts.displayError ? 'fade-out' : ''}`}
                    onClose={() => setDisplayError(false)}
                >
                    {error}
                </Alert>
            )}
            {displaySuccess && (
                <Alert
                    variant="success"
                    dismissible
                    className={`status-alert ${fadingAlerts.displaySuccess ? 'fade-out' : ''}`}
                    onClose={() => setDisplaySuccess(false)}
                >
                    {successMessage}
                </Alert>
            )}

            {loadingNotes ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <>
                    <NewNote
                        newNoteText={newNoteText}
                        setNewNoteText={setNewNoteText}
                        creatingNoteOperation={creatingNoteOperation}
                        onCreateNote={handleCreateNote}
                    />
                    {notes?.length > 0 && (
                        <div className="existing-notes" style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            padding: '10px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px'
                        }}>
                            {notes.map((note) => {
                                return (
                                    <NoteItem
                                        key={note.id}
                                        note={note}
                                        id={note.id}
                                        userId={userId}
                                        editMode={editMode}
                                        deleteMode={deleteMode}
                                        noteLoading={noteLoading}
                                        onEnableEditMode={handleEnableEditMode}
                                        onExitEditMode={handleExitEditMode}
                                        onEnableDeleteMode={handleEnableDeleteMode}
                                        onExitDeleteMode={handleExitDeleteMode}
                                        onDeleteNote={handleDeleteNote}
                                        onUpdateNote={handleUpdateNote}
                                        onChangeNote={handleChangeNote}
                                        formatDate={formatUnixTimestampToHumanReadableFormat}
                                    />
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

NotesContainer.propTypes = {
    surveyYear: PropTypes.string.isRequired,
    companyId: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    participantName: PropTypes.string.isRequired,
    onError: PropTypes.func.isRequired
};

export default NotesContainer; 